import { matchPath } from "react-router-dom";
import { z } from "zod";
import { UserTypeEnum } from "../types/common";
import {
  DEFAULT_USER_PERMISSIONS,
  FULL_ACCESS_USER_TYPES,
  mergePartialPermissions,
  PermissionKey,
  UserPermissions,
} from "./permissions";

export type UserType = z.infer<typeof UserTypeEnum>;

const ALL_USER_TYPES = UserTypeEnum.options;

export type RoutePolicy = {
  pattern: string;
  permissionKey: PermissionKey;
};

export type NavMenuItemDef = {
  id: string;
  label: string;
  path: string;
  order: number;
  permissionKey: PermissionKey;
};

const ROUTE_POLICIES: RoutePolicy[] = [
  { pattern: "/userManagement", permissionKey: "user_management" },
  { pattern: "/clients/sites", permissionKey: "clients" },
  { pattern: "/clients", permissionKey: "clients" },
  { pattern: "/clientName/:name", permissionKey: "clients" },
  { pattern: "/clientProject/:siteName", permissionKey: "clients" },
  { pattern: "/siteVisits", permissionKey: "site_visits" },
  { pattern: "/siteConfig", permissionKey: "site_config" },
  { pattern: "/sku", permissionKey: "sku" },
  { pattern: "/ledger", permissionKey: "accounts" },
  { pattern: "/quotation/create", permissionKey: "create_boq" },
  { pattern: "/quotation/edit", permissionKey: "create_boq" },
  { pattern: "/quotation/costPrice", permissionKey: "create_boq" },
  { pattern: "/quotation", permissionKey: "create_boq" },
  { pattern: "/openQuotes", permissionKey: "pending_payments" },
  { pattern: "/accounts", permissionKey: "accounts" },
  { pattern: "/boq/create", permissionKey: "create_boq" },
  { pattern: "/boq/edit", permissionKey: "create_boq" },
  { pattern: "/purchaseOrder", permissionKey: "pending_po" },
  { pattern: "/deliveryChallan/create", permissionKey: "dispatch_dc" },
  { pattern: "/deliveryChallan/edit", permissionKey: "dispatch_dc" },
  { pattern: "/deliveryChallan", permissionKey: "dispatch_dc" },
  { pattern: "/create/replacement", permissionKey: "rep_pending_po" },
  { pattern: "/replacementDC", permissionKey: "rep_pending_po" },
  { pattern: "/warehouse/purchaseOrder", permissionKey: "pending_po" },
  { pattern: "/pendingReplacement", permissionKey: "rep_pending_po" },
  { pattern: "/warehouse/ready-for-dispatch-po", permissionKey: "rfd_po" },
  { pattern: "/dc/dispatch", permissionKey: "dispatch_dc" },
  { pattern: "/sampleDC", permissionKey: "dispatch_dc" },
  { pattern: "/create/sampleDC", permissionKey: "dispatch_dc" },
  { pattern: "/sales-return/create", permissionKey: "dispatch_dc" },
  { pattern: "/sales-return/duplicate", permissionKey: "dispatch_dc" },
  { pattern: "/tasks", permissionKey: "tasks" },
  { pattern: "/salesReport", permissionKey: "sales_report" },
  { pattern: "/ongoingSites", permissionKey: "ongoing_sites" },
  { pattern: "/product-catalog", permissionKey: "catalogue" },
  { pattern: "/petty-cash", permissionKey: "petty_cash" },
  { pattern: "/payments", permissionKey: "payments" },
];

const SORTED_ROUTE_POLICIES = [...ROUTE_POLICIES].sort(
  (a, b) => b.pattern.length - a.pattern.length
);

const USER_MANAGEMENT_ALLOWED_USER_ID = "01J0XG7BET88PVW0T993KCGTES";

const canAccessUserManagement = (userId: string | null | undefined) =>
  userId === USER_MANAGEMENT_ALLOWED_USER_ID;

export const NAV_MENU_ITEMS: NavMenuItemDef[] = [
  {
    id: "create-boq",
    label: "Create BOQ",
    path: "/boq/create",
    order: 10,
    permissionKey: "create_boq",
  },
  {
    id: "pending-po",
    label: "Pending PO",
    path: "/warehouse/purchaseOrder",
    order: 20,
    permissionKey: "pending_po",
  },
  {
    id: "rep-pending-po",
    label: "Rep Pending PO",
    path: "/pendingReplacement",
    order: 30,
    permissionKey: "rep_pending_po",
  },
  {
    id: "rfd-po",
    label: "RFD PO",
    path: "/warehouse/ready-for-dispatch-po",
    order: 40,
    permissionKey: "rfd_po",
  },
  {
    id: "clients",
    label: "Clients",
    path: "/clients",
    order: 50,
    permissionKey: "clients",
  },
  {
    id: "site-visits",
    label: "Site Visits",
    path: "/siteVisits",
    order: 55,
    permissionKey: "site_visits",
  },
  {
    id: "sku",
    label: "SKU",
    path: "/sku",
    order: 60,
    permissionKey: "sku",
  },
  {
    id: "site-config",
    label: "Site Config",
    path: "/siteConfig",
    order: 70,
    permissionKey: "site_config",
  },
  {
    id: "ongoing-sites",
    label: "Ongoing Sites",
    path: "/ongoingSites",
    order: 80,
    permissionKey: "ongoing_sites",
  },
  {
    id: "dispatch-dc",
    label: "Dispatch DC",
    path: "/dc/dispatch",
    order: 10,
    permissionKey: "dispatch_dc",
  },
  {
    id: "tasks",
    label: "Tasks",
    path: "/tasks",
    order: 90,
    permissionKey: "tasks",
  },
  {
    id: "pending-payments",
    label: "Pending Payments",
    path: "/openQuotes",
    order: 110,
    permissionKey: "pending_payments",
  },
  {
    id: "payments",
    label: "Payments",
    path: "/payments",
    order: 120,
    permissionKey: "payments",
  },
  {
    id: "petty-cash",
    label: "Petty Cash",
    path: "/petty-cash",
    order: 130,
    permissionKey: "petty_cash",
  },
  {
    id: "accounts",
    label: "Accounts",
    path: "/accounts",
    order: 140,
    permissionKey: "accounts",
  },
  {
    id: "sales-report",
    label: "Sales Report",
    path: "/salesReport",
    order: 150,
    permissionKey: "sales_report",
  },
  {
    id: "catalogue",
    label: "Catalogue",
    path: "/product-catalog",
    order: 160,
    permissionKey: "catalogue",
  },
  {
    id: "user-management",
    label: "User Management",
    path: "/userManagement",
    order: 170,
    permissionKey: "user_management",
  },
];

export function parseUserPermissions(
  value: string | null | undefined
): UserPermissions {
  if (!value) {
    return DEFAULT_USER_PERMISSIONS;
  }
  try {
    return mergePartialPermissions(JSON.parse(value));
  } catch (error) {
    return DEFAULT_USER_PERMISSIONS;
  }
}

export function getMenuItemsForRole(
  userType: string | null | undefined,
  userPermissions: UserPermissions,
  userId: string | null | undefined
): { id: string; label: string; path: string }[] {
  if (!userType || !UserTypeEnum.safeParse(userType).success) {
    return [];
  }
  if (FULL_ACCESS_USER_TYPES.includes(userType)) {
    return NAV_MENU_ITEMS.filter(
      (item) =>
        item.path !== "/userManagement" || canAccessUserManagement(userId)
    )
      .sort((a, b) => a.order - b.order)
      .map(
      ({ id, label, path }) => ({ id, label, path })
      );
  }
  return NAV_MENU_ITEMS.filter(
    (item) =>
      userPermissions[item.permissionKey] &&
      (item.path !== "/userManagement" || canAccessUserManagement(userId))
  )
    .sort((a, b) => a.order - b.order)
    .map(({ id, label, path }) => ({ id, label, path }));
}

export function getDefaultRedirectPath(
  userType: string | null | undefined,
  userPermissions: UserPermissions,
  userId: string | null | undefined
): string {
  const items = getMenuItemsForRole(userType, userPermissions, userId);
  if (items.length > 0) {
    return items[0].path;
  }
  return "/tasks";
}

export function canAccessPath(
  pathname: string,
  userType: string | null | undefined,
  userPermissions: UserPermissions,
  userId: string | null | undefined
): boolean {
  if (!userType || !UserTypeEnum.safeParse(userType).success) {
    return false;
  }
  if (pathname === "/userManagement") {
    return canAccessUserManagement(userId);
  }

  if (FULL_ACCESS_USER_TYPES.includes(userType)) {
    return true;
  }
  for (const rule of SORTED_ROUTE_POLICIES) {
    const m = matchPath({ path: rule.pattern, end: true }, pathname);
    if (m) {
      return userPermissions[rule.permissionKey];
    }
  }
  return false;
}
